import { SvgIconProps } from '@material-ui/core';
import { FunctionComponent, Key, ReactElement, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { SidebarMenuLinkLegacy } from './AppSidebarMenuLegacy';
import styled from 'styled-components';
import { Color } from '@superdispatch/ui';

const SidebarMenuBoxLegacy = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 8px;
  margin-bottom: 4px;
  font-size: 13px;

  &[data-dense='true'] {
    padding: 6px 8px;
    margin-bottom: 0px;
    font-size: 12px;
  }

  width: 100%;
  border-radius: 3px;

  color: ${Color.Silver500};
  text-decoration: none;
  letter-spacing: -0.5px;
  line-height: 16px;

  &:hover {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.2);
  }

  &[aria-current='page'] {
    color: ${Color.White};
    background-color: rgba(91, 99, 113, 0.5);
  }
`;

const HiddenSidebarMenuItem = styled.li`
  display: none;
`;

const StyledListItem = styled.li`
  padding-left: 8px;
  padding-right: 8px;
`;

export interface SidebarLinkLegacy {
  key: Key;
  href: string;
  label: string;
  hidden?: boolean;
  localPath?: string | undefined;
  Icon?: FunctionComponent<SvgIconProps>;
  RightIcon?: FunctionComponent<SvgIconProps>;
  badgeContent?: number | string;
  badgeType?: 'dot' | 'standard';
  RenderComponent?: FunctionComponent<{ link?: SidebarLinkLegacy }>;
  target?: '_blank';
  onClick?: () => void;
  hasError?: boolean;
  tag?: ReactElement;
  disabledLink?: boolean;
  dataIntercomTarget?: string;
}

interface Props {
  itemRef?: (el: HTMLLIElement | null) => void;
  link: SidebarLinkLegacy;
  children: ReactNode;
  onClick?: () => void;
  dataIntercomTarget?: string;
  hidden?: boolean;
}

export function AppSidebarMenuItem({
  link,
  children,
  dataIntercomTarget,
  onClick,
  itemRef,
  hidden,
}: Props) {
  const { RenderComponent } = link;

  const content = RenderComponent ? (
    <RenderComponent link={link} />
  ) : link.disabledLink ? (
    <SidebarMenuBoxLegacy onClick={link.onClick} data-dense={true}>
      {children}
    </SidebarMenuBoxLegacy>
  ) : link.localPath ? (
    <SidebarMenuLinkLegacy
      as={NavLink}
      to={link.localPath}
      onClick={link.onClick}
      data-dense={true}
    >
      {children}
    </SidebarMenuLinkLegacy>
  ) : (
    <SidebarMenuLinkLegacy
      href={link.href}
      onClick={link.onClick}
      data-dense={true}
    >
      {children}
    </SidebarMenuLinkLegacy>
  );

  if (hidden) {
    return (
      <HiddenSidebarMenuItem
        ref={itemRef}
        data-cy={`${link.key}_sidebar_item`}
        data-intercom-target={dataIntercomTarget}
      >
        {content}
      </HiddenSidebarMenuItem>
    );
  }

  return (
    <StyledListItem
      ref={itemRef}
      onClick={onClick}
      data-cy={`${link.key}_sidebar_item`}
      data-intercom-target={dataIntercomTarget}
    >
      {content}
    </StyledListItem>
  );
}
